import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IMPORTED_LISTING_REQUEST_MAX_CARDS,
  IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION,
  IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION,
  IMPORTED_LISTING_UI_MAX_CARDS,
  type ImportedListingApplyResult,
  type ImportedListingPreviewResponse,
  type ImportedListingPreviewResult,
} from '@tcvs/shared';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { useMarketplaceApi } from '../marketplace/marketplace-api';
import {
  ImportedListingChangeDetails,
  supportsImportedListingPreview,
} from './ImportedListingChangeDetails';

type ImportedListingIdentityMismatch = NonNullable<
  ImportedListingPreviewResult['identityMismatch']
>;

export type ImportedListingApi = Pick<
  ReturnType<typeof useMarketplaceApi>,
  'previewImportedListing' | 'applyImportedListing'
>;
type RosterCard = { cardId: string; shortCode?: string; retiredAt?: string };
type ReviewScope = 'listing' | 'presentation';
type OutcomeHistory = Record<string, Partial<Record<ReviewScope, string>>>;
const emptyScopeSets = (): Record<ReviewScope, Set<string>> => ({
  listing: new Set(),
  presentation: new Set(),
});
type ApplyExecution = {
  batchId: string;
  scope: ReviewScope;
  previews: ImportedListingPreviewResponse[];
  results: ImportedListingApplyResult[];
  pending?: string[];
};
type Props = {
  batchId: string;
  cards: readonly RosterCard[];
  disabled?: boolean;
  /** Monotonic Workbench visit, including leave-and-return to the same Card. */
  reviewVisit?: number;
  hasUnsavedChanges: () => boolean;
  onApplied: () => Promise<unknown>;
};

export function BulkImportedListingTools(props: Props) {
  const { claims } = useAuth();
  const api = useMarketplaceApi();
  if (!claims || !hasAnyGroup(claims, ['reviewer', 'admin'])) return null;
  return <BulkImportedListingPanel key={props.batchId} {...props} api={api} />;
}

function completeRows(ids: string[], results: { cardId: string }[]): boolean {
  return (
    results.length === ids.length &&
    new Set(results.map((row) => row.cardId)).size === ids.length &&
    results.every((row) => ids.includes(row.cardId))
  );
}

const IDENTITY_DIFFERENCE_FIELDS = {
  category: ['Category', 'string'],
  set: ['Set', 'string'],
  number: ['Card number', 'string'],
  name: ['Name', 'string'],
  year: ['Year', 'string'],
  brand: ['Brand', 'string'],
  subset: ['Subset', 'string'],
  attributes: ['Attributes', 'string'],
  team: ['Team', 'string'],
  language: ['Language', 'string'],
  variation: ['Variation', 'string'],
  rookie: ['Rookie', 'boolean'],
  autograph: ['Autograph', 'boolean'],
  memorabilia: ['Memorabilia', 'boolean'],
  serialNumbered: ['Serial numbered', 'boolean'],
  serialTo: ['Print run', 'number'],
} as const;
function IdentityDifference({ diagnostic }: { diagnostic?: ImportedListingIdentityMismatch }) {
  const plain = (value: unknown): value is Record<string, unknown> =>
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype;
  const valid = (value: unknown, kind: string, max: number, currentChecklist = false): boolean => {
    if (!plain(value)) return false;
    if (currentChecklist && value.state === 'REMOVED_BY_CORRECTION')
      return Object.keys(value).length === 1;
    if (value.state === 'missing') return Object.keys(value).length === 1;
    if (
      value.state !== 'value' ||
      Object.keys(value).length !== 2 ||
      !Object.hasOwn(value, 'value')
    )
      return false;
    return kind === 'string'
      ? typeof value.value === 'string' && value.value.length <= max
      : kind === 'boolean'
        ? typeof value.value === 'boolean'
        : typeof value.value === 'number' &&
          Number.isSafeInteger(value.value) &&
          value.value > 0 &&
          value.value <= 1000000;
  };
  const supported =
    plain(diagnostic) &&
    Object.keys(diagnostic).length === 4 &&
    diagnostic.code === 'SOURCE_IDENTITY_MISMATCH' &&
    typeof diagnostic.field === 'string' &&
    Object.hasOwn(IDENTITY_DIFFERENCE_FIELDS, diagnostic.field) &&
    ['confirmedCard', 'currentChecklist'].every((key) => Object.hasOwn(diagnostic, key));
  const field = supported ? diagnostic.field : undefined;
  const meta = field ? IDENTITY_DIFFERENCE_FIELDS[field] : undefined;
  const usable =
    meta &&
    diagnostic &&
    valid(diagnostic.confirmedCard, meta[1], field === 'attributes' ? 1000 : 300) &&
    valid(diagnostic.currentChecklist, meta[1], field === 'attributes' ? 1000 : 300, true);
  const display = (value: ImportedListingIdentityMismatch['currentChecklist']) =>
    value.state === 'REMOVED_BY_CORRECTION'
      ? 'Removed by checklist correction'
      : value.state === 'missing'
        ? 'Not present'
        : typeof value.value === 'string'
          ? `“${value.value}”`
          : String(value.value);
  return (
    <section aria-label="Source identity review">
      {usable ? (
        <>
          <h4>First identity difference: {meta[0]}</h4>
          <dl>
            <dt>Confirmed Card value</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{display(diagnostic.confirmedCard)}</dd>
            <dt>Current checklist value</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{display(diagnostic.currentChecklist)}</dd>
          </dl>
        </>
      ) : (
        <p>First identity difference details are unavailable.</p>
      )}
      <p>
        Review the Card identity and checklist source against the physical card before trying again.
        Imported listing changes remain blocked.
      </p>
    </section>
  );
}

/** Mount, navigation and selection never issue listing mutations. */
export function BulkImportedListingPanel({
  batchId,
  cards,
  api,
  disabled = false,
  reviewVisit = 0,
  hasUnsavedChanges,
  onApplied,
}: Props & { api: ImportedListingApi }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set<string>());
  const [previews, setPreviews] = useState<ImportedListingPreviewResponse[]>([]);
  const [results, setResults] = useState<ImportedListingApplyResult[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [includePresentation, setIncludePresentation] = useState(false);
  const [previewComplete, setPreviewComplete] = useState(false);
  const [startedApply, setStartedApply] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadPending, setReloadPending] = useState(false);
  const [traversed, setTraversed] = useState(emptyScopeSets);
  const [completed, setCompleted] = useState(emptyScopeSets);
  const [roundIds, setRoundIds] = useState<string[]>([]);
  const [roundNumber, setRoundNumber] = useState(0);
  const [history, setHistory] = useState<OutcomeHistory>({});
  const [previousPreview, setPreviousPreview] = useState<Partial<Record<ReviewScope, string>>>({});
  const reviewScope: ReviewScope = includePresentation ? 'presentation' : 'listing';
  const remember = (rows: { cardId: string; status: string }[], scope: ReviewScope) => {
    setHistory((current) => {
      const next = { ...current };
      for (const row of rows) next[row.cardId] = { ...next[row.cardId], [scope]: row.status };
      return next;
    });
    const verified = rows.filter((row) => row.status === 'APPLIED' || row.status === 'UNCHANGED');
    if (verified.length)
      setCompleted((current) => ({
        ...current,
        [scope]: new Set([...current[scope], ...verified.map((row) => row.cardId)]),
      }));
  };
  const [, setExpiryClock] = useState(0);
  const roster = [
    ...new Map(
      cards.filter((card) => card.retiredAt === undefined).map((card) => [card.cardId, card]),
    ).values(),
  ];
  const rosterIds = new Set(roster.map((card) => card.cardId));
  const scopeKey = JSON.stringify([batchId, reviewVisit, [...rosterIds]]);
  // Object identity distinguishes A→B→A, even before a request's finally runs.
  const requestScope = useRef({ key: scopeKey });
  if (requestScope.current.key !== scopeKey) requestScope.current = { key: scopeKey };
  const previewScope = useRef<typeof requestScope.current | null>(null);
  const previewedIds = useRef<string[]>([]);
  const execution = useRef<ApplyExecution | null>(null);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const currentBatch = useRef(batchId);
  currentBatch.current = batchId;
  const latestGuard = useRef(hasUnsavedChanges);
  latestGuard.current = hasUnsavedChanges;
  const reloadGeneration = useRef(0);
  const reloadScope = `${batchId}|${reviewVisit}`;
  const latestReloadScope = useRef(reloadScope);
  latestReloadScope.current = reloadScope;
  const latestOnApplied = useRef(onApplied);
  latestOnApplied.current = onApplied;
  const previousBatch = useRef(batchId);
  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setBusy(false);
    previewScope.current = null;
    const active = execution.current;
    if (active?.pending?.length || active?.results.some((row) => row.status === 'UNKNOWN')) {
      const pending = active.pending ?? [];
      active.results = [
        ...active.results.filter((row) => !pending.includes(row.cardId)),
        ...pending.map((cardId) => ({
          cardId,
          status: 'UNKNOWN' as const,
          reasons: ['The view changed while saving. Check the original uncertain result.'],
        })),
      ];
      active.pending = undefined;
      setPreviews(active.previews);
      setResults(active.results);
      setConfirmed(true);
      setPreviewComplete(true);
      setStartedApply(true);
      if (active.batchId === batchId) remember(active.results, active.scope);
    } else {
      execution.current = null;
      setPreviews([]);
      setResults([]);
      setConfirmed(false);
      setPreviewComplete(false);
      setStartedApply(false);
    }
    setSelected((current) => new Set([...current].filter((id) => rosterIds.has(id))));
    if (previousBatch.current !== batchId) {
      previousBatch.current = batchId;
      setSelected(new Set());
      setTraversed(emptyScopeSets());
      setCompleted(emptyScopeSets());
      setRoundIds([]);
      setRoundNumber(0);
      setHistory({});
      setPreviousPreview({});
      setIncludePresentation(false);
    }
    // The signature, rather than array identity, represents the current roster.
  }, [scopeKey]);
  useEffect(() => {
    if (!previews.length) return;
    const expires = Math.min(...previews.map((item) => Date.parse(item.expiresAt)));
    if (!Number.isFinite(expires) || expires <= Date.now()) return;
    const timer = window.setTimeout(
      () => setExpiryClock((clock) => clock + 1),
      expires - Date.now() + 1,
    );
    return () => window.clearTimeout(timer);
  }, [previews]);
  useEffect(() => {
    // An obsolete reload must neither unlock nor resurrect errors in a later visit.
    reloadGeneration.current += 1;
    setReloadPending(false);
    setError(null);
    return () => {
      reloadGeneration.current += 1;
    };
  }, [reloadScope]);
  const dialog = useRef<HTMLElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [batchId],
  );
  useEffect(() => {
    if (!open) return;
    dialog.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !inFlight.current) setOpen(false);
      if (event.key !== 'Tab') return;
      const nodes = Array.from(
        dialog.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), [tabindex="0"]',
        ) ?? [],
      );
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      opener.current?.focus();
    };
  }, [open]);
  const guard = () => {
    if (!latestGuard.current()) return true;
    setError('Save or discard your unsaved review edits before using imported listings.');
    return false;
  };
  const begin = () => {
    if (inFlight.current || disabled || reloadPending || !guard()) return null;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const requestGeneration = ++generation.current;
    const scope = requestScope.current;
    return () =>
      generation.current === requestGeneration &&
      requestScope.current === scope &&
      currentBatch.current === batchId;
  };
  const finish = (live: () => boolean) => {
    if (!live()) return;
    inFlight.current = false;
    setBusy(false);
  };
  const reload = async (callback = latestOnApplied.current, scope = latestReloadScope.current) => {
    if (scope !== latestReloadScope.current) return;
    const requestGeneration = ++reloadGeneration.current;
    const live = () =>
      reloadGeneration.current === requestGeneration && latestReloadScope.current === scope;
    if (!guard()) {
      setReloadPending(true);
      return;
    }
    setReloadPending(true);
    try {
      await callback();
      if (!live()) return;
      setReloadPending(false);
      setError(null);
    } catch {
      if (!live()) return;
      setReloadPending(true);
      setError(
        'Changes were saved, but this page could not reload them. Reload saved changes before continuing.',
      );
    }
  };
  const preview = async () => {
    if (
      !selected.size ||
      selected.size > IMPORTED_LISTING_UI_MAX_CARDS ||
      unknown ||
      [...selected].some((id) => !rosterIds.has(id))
    )
      return;
    const live = begin();
    if (!live) return;
    setPreviews([]);
    setResults([]);
    setConfirmed(false);
    setStartedApply(false);
    setPreviewComplete(false);
    const ids = [...selected].filter((id) => !verifiedIds.has(id));
    if (!ids.length) {
      setError('These selected cards already have verified outcomes. Choose unverified cards.');
      finish(live);
      return;
    }
    previewScope.current = requestScope.current;
    previewedIds.current = ids;
    execution.current = null;
    setTraversed((current) => ({
      ...current,
      [reviewScope]: new Set([...current[reviewScope], ...ids]),
    }));
    if (!roundIds.length) {
      setRoundIds(ids);
      setRoundNumber((current) => current + 1);
    }
    const observed: ImportedListingPreviewResponse['results'] = [];
    let capability: string | undefined;
    try {
      for (let offset = 0; offset < ids.length; offset += IMPORTED_LISTING_REQUEST_MAX_CARDS) {
        const cardIds = ids.slice(offset, offset + IMPORTED_LISTING_REQUEST_MAX_CARDS);
        const result = await api.previewImportedListing({
          batchId,
          cardIds,
          ...(includePresentation
            ? {
                previewVersion: IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION,
                includePresentation: true as const,
              }
            : { previewVersion: IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION }),
        });
        if (!live()) return;
        if (!result.ok) throw new Error(result.message ?? result.error);
        if (
          !result.data.previewId ||
          !Number.isFinite(Date.parse(result.data.expiresAt)) ||
          !completeRows(cardIds, result.data.results)
        )
          throw new Error('The preview was incomplete. Preview the selection again.');
        if (!supportsImportedListingPreview(result.data, includePresentation))
          throw new Error(
            'These listing changes could not be safely displayed. Refresh the page and preview again.',
          );
        const returnedCapability = JSON.stringify([
          result.data.previewVersion,
          result.data.includePresentation,
        ]);
        if (capability !== undefined && capability !== returnedCapability)
          throw new Error('The preview chunks used different versions or scopes. Preview again.');
        capability = returnedCapability;
        setPreviews((current) => [...current, result.data]);
        observed.push(...result.data.results);
        setPreviousPreview((current) => ({
          ...current,
          [reviewScope]: `${observed.length}/${ids.length} checked; ${observed.filter((row) => row.status === 'BLOCKED').length} not ready; ${observed.filter((row) => row.status === 'UNCHANGED').length} unchanged.`,
        }));
      }
      if (live()) {
        setPreviewComplete(true);
        remember(
          observed.filter((row) => row.status === 'UNCHANGED'),
          reviewScope,
        );
      }
    } catch (failure) {
      if (live())
        setError(
          `Preview stopped. ${failure instanceof Error ? failure.message : 'Please try again.'} No listing changes were applied.`,
        );
    } finally {
      finish(live);
    }
  };
  const apply = async (uncertainOnly = false) => {
    if (!previewComplete || !confirmed || (!uncertainOnly && startedApply)) return;
    if (!uncertainOnly && !currentPreview) {
      setError('The preview expired or changed. Preview the remaining selection again.');
      return;
    }
    if (uncertainOnly && !execution.current?.results.some((row) => row.status === 'UNKNOWN'))
      return;
    const live = begin();
    if (!live) return;
    setStartedApply(true);
    const appliedVisitCallback = latestOnApplied.current;
    const appliedVisitScope = latestReloadScope.current;
    let changed = false;
    if (!uncertainOnly) execution.current = { batchId, scope: reviewScope, previews, results: [] };
    const active = execution.current!;
    try {
      for (const item of active.previews) {
        if (!live()) return;
        const cardIds = item.results
          .filter((row) =>
            uncertainOnly
              ? active.results.some(
                  (outcome) => outcome.cardId === row.cardId && outcome.status === 'UNKNOWN',
                )
              : row.status === 'READY' && selected.has(row.cardId) && !verifiedIds.has(row.cardId),
          )
          .map((row) => row.cardId);
        if (!cardIds.length) continue;
        if (!uncertainOnly && Date.parse(item.expiresAt) <= Date.now())
          throw new Error('The preview expired. Preview the remaining selection again.');
        if (!guard()) break;
        let outcomes: ImportedListingApplyResult[];
        active.pending = cardIds;
        try {
          const response = await api.applyImportedListing({
            batchId: active.batchId,
            previewId: item.previewId,
            cardIds,
            attested: true,
          });
          if (!live()) return;
          if (
            !response.ok ||
            response.data.previewId !== item.previewId ||
            !completeRows(cardIds, response.data.results)
          ) {
            throw new Error('The result could not be confirmed.');
          }
          outcomes = response.data.results;
        } catch {
          if (!live()) return;
          outcomes = cardIds.map((cardId) => ({
            cardId,
            status: 'UNKNOWN',
            reasons: [
              'The result could not be confirmed. Check uncertain results before starting a new preview.',
            ],
          }));
        }
        active.pending = undefined;
        active.results = [
          ...active.results.filter((row) => !cardIds.includes(row.cardId)),
          ...outcomes,
        ];
        setResults(active.results);
        if (active.batchId === batchId) remember(outcomes, active.scope);
        const verified = outcomes
          .filter((row) => row.status === 'APPLIED' || row.status === 'UNCHANGED')
          .map((row) => row.cardId);
        changed ||= outcomes.some((row) => row.status === 'APPLIED');
        setSelected((current) => new Set([...current].filter((id) => !verified.includes(id))));
        if (outcomes.some((row) => row.status !== 'APPLIED' && row.status !== 'UNCHANGED')) {
          setError('Apply stopped. Review each result below; later cards were not attempted.');
          break;
        }
      }
      if (changed && live() && active.batchId === batchId)
        await reload(appliedVisitCallback, appliedVisitScope);
    } catch (failure) {
      if (live())
        setError(failure instanceof Error ? failure.message : 'Apply stopped. Review the results.');
      if (changed && live() && active.batchId === batchId)
        await reload(appliedVisitCallback, appliedVisitScope);
    } finally {
      finish(live);
    }
  };
  const unknown = results.some((row) => row.status === 'UNKNOWN');
  const verifiedIds = completed[reviewScope];
  const historyStatuses = Object.values(history).flatMap((row) =>
    row[reviewScope] ? [row[reviewScope]!] : [],
  );
  const remaining = roster.filter(
    (card) => !traversed[reviewScope].has(card.cardId) && !verifiedIds.has(card.cardId),
  );
  const allRows = previews.flatMap((item) => item.results);
  const currentPreview =
    previewComplete &&
    !startedApply &&
    previewScope.current === requestScope.current &&
    completeRows(previewedIds.current, allRows) &&
    previews.every(
      (item) =>
        supportsImportedListingPreview(item, includePresentation) &&
        Date.parse(item.expiresAt) > Date.now(),
    );
  const readyIds = new Set(
    allRows.filter((row) => row.status === 'READY').map((row) => row.cardId),
  );
  const readyCount = previews
    .flatMap((item) => item.results)
    .filter(
      (row) => row.status === 'READY' && selected.has(row.cardId) && !verifiedIds.has(row.cardId),
    ).length;
  const resetPlan = () => {
    previewScope.current = null;
    execution.current = null;
    setPreviews([]);
    setResults([]);
    setConfirmed(false);
    setPreviewComplete(false);
    setStartedApply(false);
    setError(null);
  };
  const canTransition = () =>
    !inFlight.current && !disabled && !unknown && !reloadPending && guard();
  const nextRound = () => {
    if (!canTransition()) return;
    const ids = remaining.slice(0, IMPORTED_LISTING_UI_MAX_CARDS).map((card) => card.cardId);
    if (!ids.length) return;
    resetPlan();
    setSelected(new Set(ids));
    setRoundIds(ids);
    setRoundNumber((current) => current + 1);
    setTraversed((current) => ({
      ...current,
      [reviewScope]: new Set([...current[reviewScope], ...ids]),
    }));
  };
  return (
    <>
      <button
        ref={opener}
        type="button"
        disabled={disabled || busy}
        onClick={() => {
          if (guard()) setOpen(true);
        }}
      >
        Use imported listing…
      </button>
      {!open && error ? <p role="alert">{error}</p> : null}
      {open
        ? createPortal(
            <div className="tcvs-batch-transfer-backdrop">
              <section
                ref={dialog}
                className="tcvs-batch-transfer-dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Use imported listing"
              >
                <header>
                  <h2>Use imported listing</h2>
                  <button type="button" disabled={busy} onClick={() => setOpen(false)}>
                    Close
                  </button>
                </header>
                <p>
                  Select up to {IMPORTED_LISTING_UI_MAX_CARDS} cards, then preview their imported
                  title, description, own asking price and supported features. Eligibility is
                  checked against the current confirmed import. Any proposed listing details or
                  category refresh is shown below.
                </p>
                <p>
                  {includePresentation
                    ? 'Only explicitly displayed condition changes replace both the saved card and its editable listing draft. The card’s identity, images, vision grade, readiness and publishing are unchanged.'
                    : 'This changes listing drafts only. The card’s identity, images, grade, condition, readiness and publishing are unchanged.'}
                </p>
                <p>
                  Cards already in readiness, approval, export or provider workflows remain
                  protected and require their normal review workflow.
                </p>
                <p>
                  {roundIds.length
                    ? `Round ${roundNumber}: ${roundIds.length} candidates; ${selected.size} selected. `
                    : 'No round started. '}
                  {remaining.length} candidates remaining in this roster. Candidates need a fresh
                  Preview; this is the current page roster, not a full live inventory snapshot.
                </p>
                <button
                  type="button"
                  disabled={disabled || busy || unknown || reloadPending || !remaining.length}
                  onClick={nextRound}
                >
                  Next 50 cards
                </button>
                {!remaining.length && traversed[reviewScope].size ? (
                  <button
                    type="button"
                    disabled={disabled || busy || unknown || reloadPending}
                    onClick={() => {
                      if (!canTransition()) return;
                      resetPlan();
                      setSelected(new Set());
                      setTraversed((current) => ({ ...current, [reviewScope]: new Set() }));
                      setCompleted((current) => ({ ...current, [reviewScope]: new Set() }));
                      setRoundIds([]);
                      setRoundNumber(0);
                    }}
                  >
                    Restart review with a fresh Preview
                  </button>
                ) : null}
                {!remaining.length && traversed[reviewScope].size ? (
                  <p>
                    Restart revisits this scope, including previous outcomes, using new research and
                    a fresh Preview. Old plans and acceptance are discarded.
                  </p>
                ) : null}
                {previousPreview[reviewScope] ? (
                  <p>
                    Last Preview: {previousPreview[reviewScope]} This is a previous observation, not
                    current eligibility.
                  </p>
                ) : null}
                {historyStatuses.length ? (
                  <p>
                    Session outcomes:{' '}
                    {historyStatuses.filter((status) => status === 'APPLIED').length} applied;{' '}
                    {historyStatuses.filter((status) => status === 'UNCHANGED').length} unchanged;{' '}
                    {
                      historyStatuses.filter(
                        (status) => !['APPLIED', 'UNCHANGED', 'UNKNOWN'].includes(status),
                      ).length
                    }{' '}
                    need review; {historyStatuses.filter((status) => status === 'UNKNOWN').length}{' '}
                    uncertain. Scope: {includePresentation ? 'including condition' : 'listing only'}
                    . Previous outcomes are not new plans.
                  </p>
                ) : null}
                {unknown && execution.current?.batchId !== batchId ? (
                  <p>
                    Uncertain results belong to the original batch {execution.current?.batchId}.
                  </p>
                ) : null}
                <div>
                  <label>
                    <input
                      type="checkbox"
                      checked={includePresentation}
                      disabled={disabled || busy || unknown || reloadPending}
                      onChange={(event) => {
                        if (!canTransition()) return;
                        setIncludePresentation(event.target.checked);
                        resetPlan();
                        setRoundIds([]);
                        setRoundNumber(0);
                      }}
                    />
                    Also use imported condition / slab information
                  </label>
                  <p>
                    Off by default. Turning this on requires a fresh preview and explicit acceptance
                    of every existing condition being replaced.
                  </p>
                </div>
                <fieldset disabled={disabled || busy || unknown || reloadPending}>
                  <legend>
                    Choose cards ({selected.size}/{IMPORTED_LISTING_UI_MAX_CARDS})
                  </legend>
                  {roster.map((card) => (
                    <label key={card.cardId} style={{ display: 'block' }}>
                      <input
                        type="checkbox"
                        checked={selected.has(card.cardId)}
                        disabled={
                          (verifiedIds.has(card.cardId) && !selected.has(card.cardId)) ||
                          (!selected.has(card.cardId) &&
                            selected.size >= IMPORTED_LISTING_UI_MAX_CARDS)
                        }
                        onChange={(event) => {
                          if (
                            !canTransition() ||
                            (verifiedIds.has(card.cardId) && event.target.checked)
                          )
                            return;
                          const checked = event.target.checked;
                          setSelected((current) => {
                            const next = new Set(current);
                            if (checked && next.size < IMPORTED_LISTING_UI_MAX_CARDS)
                              next.add(card.cardId);
                            else next.delete(card.cardId);
                            return next;
                          });
                          setConfirmed(false);
                          if (checked || !currentPreview) {
                            resetPlan();
                            setRoundIds([]);
                          }
                        }}
                      />
                      {card.shortCode ?? card.cardId}
                    </label>
                  ))}
                </fieldset>
                <button
                  type="button"
                  disabled={disabled || busy || !selected.size || unknown || reloadPending}
                  onClick={() => void preview()}
                >
                  Preview selected cards
                </button>
                <button
                  type="button"
                  disabled={
                    disabled || busy || unknown || reloadPending || !currentPreview || !readyCount
                  }
                  onClick={() => {
                    if (!canTransition()) return;
                    if (
                      !currentPreview ||
                      previews.some((item) => Date.parse(item.expiresAt) <= Date.now())
                    ) {
                      if (!busy && !unknown && !reloadPending)
                        setError(
                          'The preview expired or changed. Preview the remaining selection again.',
                        );
                      return;
                    }
                    setSelected(
                      (current) =>
                        new Set(
                          [...current].filter((id) => readyIds.has(id) && !verifiedIds.has(id)),
                        ),
                    );
                    setConfirmed(false);
                  }}
                >
                  Select eligible from this Preview
                </button>
                {error ? <p role="alert">{error}</p> : null}
                <div aria-live="polite">
                  {previews
                    .flatMap((item) => item.results)
                    .map((row) => {
                      const outcome = results.find((result) => result.cardId === row.cardId);
                      return (
                        <article key={row.cardId}>
                          <h3>
                            {cards.find((card) => card.cardId === row.cardId)?.shortCode ??
                              row.cardId}{' '}
                            · {outcome?.status ?? row.status}
                          </h3>
                          <ul>
                            {(outcome?.reasons ?? row.reasons).map((reason, index) => (
                              <li key={index}>{reason}</li>
                            ))}
                          </ul>
                          {row.status === 'BLOCKED' &&
                          row.reasons.includes('SOURCE_IDENTITY_MISMATCH') ? (
                            <IdentityDifference diagnostic={row.identityMismatch} />
                          ) : null}
                          {row.changes.map((change) => (
                            <ImportedListingChangeDetails key={change.field} change={change} />
                          ))}
                          {!outcome && startedApply && row.status === 'READY' ? (
                            <p>Not attempted.</p>
                          ) : null}
                        </article>
                      );
                    })}
                </div>
                {previewComplete && !startedApply && readyCount > 0 ? (
                  <>
                    <label>
                      <input
                        type="checkbox"
                        checked={confirmed}
                        disabled={disabled || busy || reloadPending}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      />
                      {includePresentation
                        ? 'I reviewed every displayed per-card listing change and the existing card and listing conditions, including all slab information being replaced, and want to apply them.'
                        : 'I reviewed every displayed per-card listing change, including listing details and category, and want to apply them.'}
                    </label>
                    <button
                      type="button"
                      disabled={disabled || busy || !confirmed || reloadPending}
                      onClick={() => void apply()}
                    >
                      Apply imported listing to {readyCount} eligible cards
                    </button>
                  </>
                ) : null}
                {unknown ? (
                  <button
                    type="button"
                    disabled={disabled || busy || reloadPending}
                    onClick={() => void apply(true)}
                  >
                    Check uncertain results
                  </button>
                ) : null}
                {reloadPending ? (
                  <button type="button" disabled={busy} onClick={() => void reload()}>
                    Reload saved changes
                  </button>
                ) : null}
                {busy ? <p role="status">Working…</p> : null}
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
